import { Controller } from '@hotwired/stimulus'

const ease = progress => 1 - (1 - progress) ** 3

// How far past the outermost chip the ripple travels.
const overshoot = 1.18

const ringOpacity = 0.9
const flashDuration = 450
const restingRing = 0.42

// Squeezed over the tail of the cycle, released as the ripple leaves.
const pressDepth = 0.08
const pressDuration = 420
const releaseDuration = 620

// Down is eased at both ends; back up is a damped spring.
const pressIn = progress => (1 - Math.cos(Math.PI * progress)) / 2

const pressOut = progress =>
  Math.exp(-3.6 * progress) * Math.cos(1.5 * Math.PI * progress)

// Guaranteed per cycle, so every verdict shows.
const minimums = [
  ['undeliverable', 2],
  ['risky', 2],
  ['unknown', 1]
]

// Odds a chip beyond the minimums is flagged, flat across the constellation.
const extraOdds = 0.04

// Weighted the way real results fall; `maxUnknown` caps unknowns on top.
const outcomes = [
  ['undeliverable', 0.5],
  ['risky', 0.32],
  ['unknown', 0.18]
]

const maxUnknown = 2

const shuffle = items => {
  const shuffled = [...items]

  for (let index = shuffled.length - 1; index > 0; index -= 1) {
    const swap = Math.floor(Math.random() * (index + 1))
    const held = shuffled[index]

    shuffled[index] = shuffled[swap]
    shuffled[swap] = held
  }

  return shuffled
}

export default class extends Controller {
  static classes = ['verified']
  static targets = ['canvas', 'chip', 'core', 'glow', 'ring']
  static values = {
    hold: { type: Number, default: 4200 },
    lead: { type: Number, default: 3200 },
    rest: { type: Number, default: 1600 },
    sweep: { type: Number, default: 1500 }
  }

  connect() {
    this.reducedMotionQuery = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    )

    this.chips = this.chipTargets.map(chip => ({
      distance: 0,
      element: chip,
      verified: false
    }))

    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        this.#start()
      } else {
        this.#stop()
      }
    })

    this.observer.observe(this.element)
  }

  disconnect() {
    this.observer.disconnect()
    this.#stop()
  }

  get #cycle() {
    return this.sweepValue + this.holdValue + this.restValue
  }

  #start() {
    if (this.reducedMotionQuery.matches) {
      this.#settle()

      return
    }

    if (this.frame) return

    // One lead-in short of a boundary, so the first ripple waits out the
    // chips' staggered entrance however short `rest` is.
    this.startedAt = performance.now() + this.leadValue - this.#cycle
    this.cycleIndex = null
    this.frame = requestAnimationFrame(now => this.#tick(now))
  }

  #stop() {
    cancelAnimationFrame(this.frame)

    this.frame = null
    this.#clear()
    this.#drawRing(0, 0)
    this.coreTarget.style.scale = 1
  }

  // Phases derive from cycle position, so a dropped frame or a scroll away
  // and back can never leave the scene half verified.
  #tick(now) {
    const total = now - this.startedAt
    const index = Math.floor(total / this.#cycle)
    const elapsed = total % this.#cycle

    if (index !== this.cycleIndex) {
      this.cycleIndex = index
      this.#measure()
      this.#roll()
    }

    if (elapsed < this.sweepValue) {
      this.#sweep(elapsed / this.sweepValue)
    } else {
      this.#drawRing(0, 0)

      if (elapsed >= this.sweepValue + this.holdValue) this.#clear()
    }

    this.#drawGlow(elapsed)
    this.#drawCore(elapsed)
    this.frame = requestAnimationFrame(next => this.#tick(next))
  }

  #sweep(progress) {
    const radius = ease(progress) * this.reach

    // Fading against distance rather than time keeps the ring legible: the
    // deceleration spends most of a ripple's life at its outer radii.
    this.#drawRing(radius, ringOpacity * (1 - radius / this.reach) ** 0.6)

    this.chips.forEach(chip => {
      if (chip.verified || chip.distance > radius) return

      chip.verified = true
      chip.element.classList.add(this.verifiedClass)
    })
  }

  #clear() {
    this.chips.forEach(chip => {
      if (!chip.verified) return

      chip.verified = false
      chip.element.classList.remove(this.verifiedClass)
    })
  }

  // Only at the boundary: `#clear()` ran a whole `rest` earlier, so no badge
  // is caught changing glyph mid-fade.
  #roll() {
    const verdicts = []
    let unknowns = 0

    minimums.forEach(([outcome, count]) => {
      for (let n = 0; n < count; n += 1) verdicts.push(outcome)

      if (outcome === 'unknown') unknowns = count
    })

    // Below `lg` the outer ring is dropped, and a verdict landing there
    // would spend the cycle unseen.
    const visible = shuffle(
      this.chips.filter(chip => chip.distance !== Infinity)
    )

    for (let n = verdicts.length; n < visible.length; n += 1) {
      if (Math.random() >= extraOdds) continue

      let outcome = this.#pick()

      if (outcome === 'unknown') {
        if (unknowns < maxUnknown) {
          unknowns += 1
        } else {
          outcome = 'risky'
        }
      }

      verdicts.push(outcome)
    }

    this.chips.forEach(chip => {
      chip.element.dataset.result = 'deliverable'
    })

    visible.slice(0, verdicts.length).forEach((chip, index) => {
      chip.element.dataset.result = verdicts[index]
    })
  }

  #pick() {
    let roll = Math.random()

    for (const [outcome, weight] of outcomes) {
      roll -= weight

      if (roll < 0) return outcome
    }

    return outcomes[0][0]
  }

  // Against the canvas, not the layout box, and per cycle rather than once
  // at connect, so the ripple stays in step through resizes.
  #measure() {
    const box = this.canvasTarget.getBoundingClientRect()
    const centerX = box.left + box.width / 2
    const centerY = box.top + box.height / 2
    let furthest = 0

    this.chips.forEach(chip => {
      const rect = chip.element.getBoundingClientRect()

      // Chips dropped below `lg` measure as a zero-size box at the document
      // origin, dragging the ripple's reach off canvas.
      if (!rect.width) {
        chip.distance = Infinity

        return
      }

      chip.distance = Math.hypot(
        rect.left + rect.width / 2 - centerX,
        rect.top + rect.height / 2 - centerY
      )
      furthest = Math.max(furthest, chip.distance)
    })

    this.reach = furthest * overshoot
  }

  #drawRing(radius, opacity) {
    this.ringTarget.style.width = `${radius * 2}px`
    this.ringTarget.style.height = `${radius * 2}px`
    this.ringTarget.style.opacity = opacity
  }

  // The squeeze belongs to the end of the cycle before the ripple it
  // releases.
  #drawCore(elapsed) {
    const charge = elapsed - (this.#cycle - pressDuration)
    let depth = 0

    if (charge >= 0) {
      depth = pressDepth * pressIn(charge / pressDuration)
    } else if (elapsed < releaseDuration) {
      depth = pressDepth * pressOut(elapsed / releaseDuration)
    }

    this.coreTarget.style.scale = 1 - depth
  }

  #drawGlow(elapsed) {
    const decay = Math.min(elapsed / flashDuration, 1)

    this.glowTarget.style.opacity = 0.5 + 0.5 * (1 - decay)
    this.glowTarget.style.scale = 1 + 0.25 * (1 - decay)
  }

  #settle() {
    this.#measure()
    this.#roll()

    this.chips.forEach(chip => {
      chip.verified = true
      chip.element.classList.add(this.verifiedClass)
    })

    this.#drawRing(this.reach * restingRing, 0.18)
    this.glowTarget.style.opacity = 0.5
    this.coreTarget.style.scale = 1
  }
}
